import { useState, useEffect } from "react";
import { useNavigate, useLocation } from "react-router-dom";
import { Grid } from "@mui/material";
import NoSleep from 'nosleep.js';

import { ApiService } from "../../services";
import { ApiConfig } from '../../configuration';
import { Form, Materials, Operations, DB, Constant, UpdateStatus, ObservationImages, TranslationConfig } from "../../constants";
import { MaterialButton, LoadingComponent, ProgressBarComponent, CustomizedDialog } from "../../components";
import { DetectNetwork, DateGenerator, TranslateStringValue, BusinessLogic, aesUtil } from "../../utilities";
import NavigationBar from "../NavigationBar";
import { CheckOs, DataSynchronization } from "../../utilities";
import "./style.css";
import { useTranslation } from 'react-i18next';

var noSleep = new NoSleep();

const HomePage = () => {
    const navigate = useNavigate();
    const { state } = useLocation()
    const { online } = DetectNetwork();
    const { t: translate } = useTranslation();

    const [isUpdated, setIsUpdated] = useState(false)
    const [allMaterials, setAllMaterials] = useState([])
    const [loading, setLoading] = useState(false);
    const [loadingText, setLoadingText] = useState("");
    const [loadingProgress, setLoadingProgress] = useState(-1)
    const [loadingMessage, setLoadingMessage] = useState("")
    const [isError, setIsError] = useState(false)
    const [alertMessage, setAlertMessage] = useState(false)
    const [dialogBtnLabel, setDialogBtnLabel] = useState("")
    const [dialogBody, setDialogBody] = useState(<p></p>)
    const [showActionButton, setShowActionButton] = useState(false)
    let currentProgress = 0
    const languageList = ['en_GB', 'fr_FR'];

    const onDownloadProgress = (e) => {
        const total = e.event.lengthComputable ? e.total : 4194304 // since most of API can't get total data size, we gonna assume the biggest total size here is 4mb
        const current = Math.round((e.loaded * 100) / total)
        if (current > currentProgress) {
            setLoadingProgress(current)
            currentProgress = current
        }
        else {
            setLoadingProgress(currentProgress)
        }
    }

    const fetchTranslationConfig = async () => {
        try {
            let translationConfigs = [];
            for (let lang of languageList) {
                const res = await ApiService.get(ApiConfig.languageBaseUrlApi + lang);
                if (res.status !== 200) {
                    setIsError(true);
                    return null;
                }
                translationConfigs.push([res.data, lang.replace('_','-')]);
            }
            
            return translationConfigs;
        } catch (e) {
            setIsError(true);
            return null;
        }
    }

    const fetchForm = async () => {
        try {
            let last_update = localStorage.getItem(Constant.localStorageKey.lastUpdate)
            if (!last_update) {
                last_update = "0000-00-00 00:00:00"
                localStorage.setItem(Constant.localStorageKey.lastUpdate, "0000-00-00 00:00:00")
            }
            setLoadingMessage("Téléchargement des formulaires...")
            const res = await ApiService.get(`ws_getforms.php?last_update=${last_update}&no_template=ok`, { onDownloadProgress: onDownloadProgress })
            if (res.status !== 200) {
                setIsError(true)
                return null
            }
            setLoadingProgress(100)
            return res.data
        } catch (e) {
            setIsError(true)
            return null
        }
    }

    const fetchMaterial = async (isMaterialDataEmpty = false, isSync) => {
        try {
            let last_update = localStorage.getItem(Constant.localStorageKey.lastUpdate)
            if (!last_update) {
                last_update = "0000-00-00 00:00:00"
                localStorage.setItem(Constant.localStorageKey.lastUpdate, "0000-00-00 00:00:00")
            }

            let materials = []
            let start = -5000
            let currentResponseLength = 0
            let allMaterial = 0
            let id = 1;
            do {
                let materialsPerQuotaRequest = []
                setLoadingProgress(0)
                start += 5000
                if (start > allMaterial) {
                    break
                }
                setLoadingMessage(`Téléchargement des matériels ${start / 5000}/${allMaterial === 0 ? "2" : Math.round(allMaterial / 5000) + 1}...`)
                const res = await ApiService.get(`ws_getmateriels.php?debut=${start}&last_update=${last_update}&valeursnonvides=ok`, { onDownloadProgress: onDownloadProgress })
                setLoadingProgress(100)
                if (res.status !== 200) {
                    return []
                }

                allMaterial = res.data["max_materiels"]
                for (let [key, value] of Object.entries(res.data)) {
                    if (key !== "max_materiels") {
                        if (isSync) {
                            materials.push(value);
                        }
                        materialsPerQuotaRequest.push(value);
                    }
                }
                if (isMaterialDataEmpty){
                    await DB.materialsData.add({
                        material: materialsPerQuotaRequest,
                        userId: localStorage.getItem(Constant.localStorageKey.userId)
                    })
                    isMaterialDataEmpty = false;
                } else {
                    await DB.materialsData.update(id, materialsData => {
                        materialsData.material = materialsData.material.concat(materialsPerQuotaRequest);
                        return materialsData;
                    })
                }
                
                currentResponseLength = Object.keys(res.data).length 
            } while (currentResponseLength !== 1)

            return materials
        } catch (e) {
            setIsError(true)
            return []
        }
    }

    const fetchOperation = async () => {
        try {
            let last_update = localStorage.getItem(Constant.localStorageKey.lastUpdate)
            if (!last_update) {
                last_update = "0000-00-00 00:00:00"
                localStorage.setItem(Constant.localStorageKey.lastUpdate, "0000-00-00 00:00:00")
            }

            setLoadingMessage("Téléchargement des exploitants...")
            setLoadingProgress(0)
            const res = await ApiService.get(`ws_getexploitants.php?last_update=${last_update}`, { onDownloadProgress: onDownloadProgress })
            setLoadingProgress(100)
            if (res.status !== 200) {
                setIsError(true)
                return null
            }

            return res.data
        } catch (e) {
            setIsError(true)
            return null
        }
    }

    const getForm = async (isSync) => {
        let isCache = true
        let formData = null
        let id = 0
        const formsDataQuery = await DB.formsData.toArray();
        if (formsDataQuery.length > 0) {
            formsDataQuery.forEach(query => {
                if (query.userId === localStorage.getItem(Constant.localStorageKey.userId)) {
                    Form.setForm(query.data);
                    id = query.id
                }
            })
        }

        if (!isSync) {
            formData = Form.getForm();
        }

        if (!formData) {
            isCache = false
            localStorage.setItem(Constant.localStorageKey.autoSync, false)
            formData = await fetchForm();
            if (formData) {
                Form.setForm(formData)
                //await formsData.clear()
                if (id === 0)
                    await DB.formsData.add({
                        data: formData,
                        userId: localStorage.getItem(Constant.localStorageKey.userId)
                    });
                else {
                    await DB.formsData.update(id, {
                        data: formData,
                        userId: localStorage.getItem(Constant.localStorageKey.userId)
                    });
                }
            }
            else {
                formData = {}
            }
        }

        const keys = Object.keys(formData);
        let materialsParameter = {}
        keys.forEach(key => {
            const formParameter = formData[key]["_parametres_"]
            materialsParameter[formParameter.bouton_materiel] = { title: formParameter.bouton_materiel, icon: formParameter.icone_url, category: formParameter.bouton_categorie }
        })

        let category = {}
        for (let [_, value] of Object.entries(materialsParameter)) {
            if (value.title === "" || !value.category || !value.icon) {
                continue
            }
            category = { ...category, [value.category]: [] }
        }

        for (let [_, value] of Object.entries(materialsParameter)) {
            if (value.title === "" || !value.category || !value.icon) {
                continue
            }

            if (category[value.category]) {
                if (value.category === "appareils et accessoires de levage") {
                    category[value.category].push(<Grid item key={value.title} xs={4} md={2} xl={2}>
                        <MaterialButton
                            title={value.title}
                            icon={value.icon.slice(2, value.icon.length)}
                            type=""
                            onClick={() => selectMaterial(value.title)}
                        />
                    </Grid>)
                }
                else {
                    category[value.category].push(<Grid item key={value.title} xs={4} md={2} xl={2}>
                        <MaterialButton
                            title={value.title}
                            icon={value.icon.slice(2, value.icon.length)}
                            type="other"
                            onClick={() => selectMaterial(value.title)}
                        />
                    </Grid>)
                }
            }
        }

        const allMaterial = []
        for (let [key, value] of Object.entries(category)) {
            allMaterial.push({ category: key, elements: value })
        }

        setAllMaterials(allMaterial)

        return { isCache }
    }

    const getMaterial = async () => {
        let isCache = true
        const materialsDataQuery = await DB.materialsData.toArray();
        if (materialsDataQuery.length > 0) {
            let isUserFound = false
            materialsDataQuery.forEach(query => {
                if (query.userId === localStorage.getItem(Constant.localStorageKey.userId)) {
                    isUserFound = true
                    Materials.setMaterials(query.material);
                }
            })

            /* Material is the same for all user, so no need to refech */
            if (!isUserFound) {
                const materials = materialsDataQuery[0].material
                await DB.materialsData.add({
                    material: materials,
                    userId: localStorage.getItem(Constant.localStorageKey.userId)
                });
                Materials.setMaterials(materials)
                isCache = false
            }
        }

        if (!Materials.getMaterials()) {
            isCache = false
            await fetchMaterial(true, false)

            if ((await DB.materialsData.toArray()).length > 0){
                Materials.setMaterials((await DB.materialsData.toArray())[0].material)
            }
            else {
                Materials.setMaterials([])
            }
        }

        return { isCache }
    }

    const getTranslationConfigs = async () => {
        const translationConfigDataQuery = await DB.translationConfigData.toArray();

        if (translationConfigDataQuery.length > 0) {
            const translationConfigs = translationConfigDataQuery[0]
            TranslationConfig.setTranslationConfig(translationConfigs);
        }
        if (!TranslationConfig.getTranslationConfig()) {
            const translationConfigs = await fetchTranslationConfig();
            if ( translationConfigs.length > 0 ) {
                for (let config of translationConfigs){
                    await DB.translationConfigData.add({
                        config: config[0],
                        language: config[1]
                    });
                    TranslationConfig.setTranslationConfig(translationConfigs);
                    window.location.reload();
                }
            } 
            else {
                TranslationConfig.setTranslationConfig([]);
                window.location.reload();
            }
        }
    }

    const syncMaterial = async () => {
        let currentMaterial = [];
        let id = 0
        const materialsDataQuery = await DB.materialsData.toArray();
        if (materialsDataQuery.length > 0) {
            materialsDataQuery.forEach(query => {
                if (query.userId === localStorage.getItem(Constant.localStorageKey.userId)) {
                    currentMaterial = query.material;
                    id = query.id
                }
            })
        }
        const newMaterials = await fetchMaterial(false, true)
        if (newMaterials.length > 0) {
            const updatedMaterial = await DataSynchronization.synchronizeMaterial(currentMaterial, newMaterials, true)
            await DB.materialsData.update(id, {
                material: updatedMaterial,
                userId: localStorage.getItem(Constant.localStorageKey.userId)
            });
            Materials.setMaterials(updatedMaterial)
        }
    }

    const syncLanguageTranslation = async () => {
        const currentTranslationConfigDataQuery = await DB.translationConfigData.toArray();
        let currentTranslationKey = 0;
        let newTranslationKey = 0;
        if (currentTranslationConfigDataQuery.length > 0) {
            for (let config of currentTranslationConfigDataQuery) {
                if (config.config.datahydrotest) {
                    currentTranslationKey += Object.keys(config.config.datahydrotest).length;
                }
            }
        }

        const newLanguageTranslationConfigs = await fetchTranslationConfig();
        if (newLanguageTranslationConfigs.length > 0) {
            for (let config of newLanguageTranslationConfigs) {
                if (config[0].datahydrotest) {
                    newTranslationKey += Object.keys(config[0].datahydrotest).length;
                }
            }
        }
        
        if (newTranslationKey > currentTranslationKey) {
            await DB.translationConfigData.clear();
            for (let config of newLanguageTranslationConfigs){
                await DB.translationConfigData.add({
                    config: config[0],
                    language: config[1]
                })
            }
        }
    }

    const linkMaterialToNewExploitant = async (materialId, exploitantId) => {
        let currentMaterial = [];
        let id = 0
        const materialsDataQuery = await DB.materialsData.toArray();
        if (materialsDataQuery.length > 0) {
            materialsDataQuery.forEach(query => {
                if (query.userId === localStorage.getItem(Constant.localStorageKey.userId)) {
                    currentMaterial = query.material;
                    id = query.id
                }
            })
        }

        for (let material of currentMaterial) {
            if (material.materiel_id === materialId) {
                material.materiel_exploitant_id = `${exploitantId}`
                break
            }
        }

        await DB.materialsData.update(id, {
            material: currentMaterial,
            userId: localStorage.getItem(Constant.localStorageKey.userId)
        });
        Materials.setMaterials(currentMaterial)
    }

    const getOperation = async () => {
        let isCache = true
        const operationDataQuery = await DB.operationData.toArray();
        if (operationDataQuery.length > 0) {
            operationDataQuery.forEach(query => {
                if (query.userId === localStorage.getItem(Constant.localStorageKey.userId)) {
                    Operations.setOperation(query.operation)
                }
            })
        }

        if (!Operations.getOperation()) {
            isCache = false
            const operations = await fetchOperation()

            if (operations) {
                await DB.operationData.add({
                    operation: operations,
                    userId: localStorage.getItem(Constant.localStorageKey.userId)
                });
                Operations.setOperation(operations)
            }
            else {
                Operations.setOperation([])
            }
        }

        return { isCache }
    }

    const syncOperation = async () => {
        let currentOperation = {};
        let id = 0
        const operationDataQuery = await DB.operationData.toArray();
        if (operationDataQuery.length > 0) {
            operationDataQuery.forEach(query => {
                if (query.userId === localStorage.getItem(Constant.localStorageKey.userId)) {
                    currentOperation = query.operation
                    id = query.id
                }
            })
        }
        const newOperations = await fetchOperation()
        if (newOperations) {
            const updatedOperation = await DataSynchronization.synchronizeOperation(currentOperation, newOperations, true)
            await DB.operationData.update(id, {
                operation: updatedOperation,
                userId: localStorage.getItem(Constant.localStorageKey.userId)
            });
            Operations.setOperation(updatedOperation)
        }
    }

    const selectMaterial = (title) => {
        navigate("/form", { state: { formName: title, prev: true } })
    }

    const submitFormWhenOnline = async () => {
        try {
            const saveFormDataQuery = await DB.saveFormData.toArray();
            const saveFormDatasByUserId = saveFormDataQuery.filter((form) => form.userId === parseInt(localStorage.getItem(Constant.localStorageKey.userId)));
            const submitFormId = []
            if (saveFormDatasByUserId.length > 0) {
                setAlertMessage(true)
                setDialogBody(<>
                    <LoadingComponent label={"Envoi du formulaire..."} />
                </>)
                setShowActionButton(false)

                const tempMaterialId = {}
                const tempExploitantId = {}
                for (let formData of saveFormDatasByUserId) {
                    if (formData.images) {
                        ObservationImages.reset()
                        for (let image of formData.images) {
                            ObservationImages.addImage(image)
                        }
                    }
                    if (formData.data) {
                        // if there is a creation of new material and exploitant when offline
                        const exploitantId = formData.data["exploitant-id"]
                        const materialId = formData.data["materiel-id"]
                        if (exploitantId.includes("PWA")) {
                            formData.data["exploitant-id"] = tempExploitantId[exploitantId] ? tempExploitantId[exploitantId] : ""
                        }
                        if (materialId.includes("PWA")) {
                            formData.data["materiel-id"] = tempMaterialId[materialId] ? tempMaterialId[materialId] : ""
                        }

                        const requestData = new FormData()
                        for(let key of Object.keys(formData.data)){
                            requestData.append(key, formData.data[key])
                        }
                        const res = await BusinessLogic.submitForm(requestData, false)
                        if (res.success) {
                            tempExploitantId[exploitantId] = res.id_exploitant
                            tempMaterialId[materialId] = res.id_materiel
                            await linkMaterialToNewExploitant(res.id_materiel, res.id_exploitant)
                            submitFormId.push(res.id_controle)
                            
                            await DB.saveFormData.delete(formData.id)
                            localStorage.setItem(Constant.localStorageKey.formSubmitted, true);
                            await updateRecord();
                        }
                    }
                }

                localStorage.setItem(Constant.localStorageKey.attempToSaveForm, false);
                //await saveFormData.clear();
                setDialogBtnLabel("OK")
                setShowActionButton(true)

                if (submitFormId.length <= 1) {
                    setDialogBody(<>
                        <p>Formulaire bien envoyé</p>
                        <p>ID rapport : {submitFormId[0]}</p>
                    </>)
                }
                else {
                    setDialogBody(<>
                        <p>Les rapports suivants ont été envoyés!</p>
                        {submitFormId.map(id => <p key={id}>ID rapport : {id}</p>)}
                    </>)
                }
            }
        } catch (e) {
            console.log(e)
            //setSaveFormError(true)
            setAlertMessage(true)
            setDialogBtnLabel("OK")
            setShowActionButton(true)
            setDialogBody(<p>Quelque chose s'est mal passé sur notre système.</p>)
        }
    }

    const updateRecord = async () => {
        setLoading(false)
        if (TranslateStringValue.translate(localStorage.getItem(Constant.localStorageKey.formSubmitted))) {
            await syncMaterial()
            await syncOperation()

            localStorage.setItem(Constant.localStorageKey.formSubmitted, false)
            const last_update = await DateGenerator.getServerDateTime()
            if (last_update) {
                localStorage.setItem(Constant.localStorageKey.lastUpdate, last_update)
            }
        }
    }

    const syncData = async () => {
        if (online) {
            setLoadingProgress(0)
            setLoading(true)

            await getForm(true)
            await syncMaterial()
            await syncOperation()
            //await updateUserInfo()
            await syncLanguageTranslation()

            localStorage.setItem(Constant.localStorageKey.autoSync, false)
            const last_update = await DateGenerator.getServerDateTime()
            if (last_update) {
                localStorage.setItem(Constant.localStorageKey.lastUpdate, last_update)
            }
            setLoading(false)
        }
        else {
            setLoading(false)
        }
    }

    const fetchData = async () => {
        setLoading(true)
        await getTranslationConfigs();
        await DateGenerator.getServerDate()
        if (TranslateStringValue.translate(localStorage.getItem(Constant.localStorageKey.isLoggedIn))) {
            const formRes = await getForm(false);
            const operationRes = await getOperation();
            const materialRes = await getMaterial();

            if (!formRes.isCache && !operationRes.isCache && !materialRes.isCache) {
                const last_update = await DateGenerator.getServerDateTime()
                if (last_update) {
                    localStorage.setItem(Constant.localStorageKey.lastUpdate, last_update)
                }
            }

            // Check submit form when online
            let attempToSaveForm = localStorage.getItem(Constant.localStorageKey.attempToSaveForm);
            if (online && attempToSaveForm == 'true') {
                submitFormWhenOnline();
            }

            if (TranslateStringValue.translate(localStorage.getItem(Constant.localStorageKey.autoSync))) {
                syncData()
            }
            else {
                setLoading(false)
            }
        }
        else {
            window.location.replace(process.env.PUBLIC_URL + "/login")
        }

        try {
            noSleep.disable();
            // document.removeEventListener('mousedown', handleWakeLock);
            // document.removeEventListener('touchstart', handleWakeLock);
        } catch (e) {
            console.log("noSleep:", e)
        }
    }

    const loadData = () => {
        setLoadingText("Loading...")
        fetchData()
    }

    const handleWakeLock = () => {
        noSleep.enable();
    };

    const checkForAppUpdate = () => {
        setLoading(true)
        setLoadingText("Vérification des mises à jour...")
        const updateDoneFlag = TranslateStringValue.translate(localStorage.getItem(Constant.localStorageKey.updateDone))
        if (updateDoneFlag) {
            setIsUpdated(updateDoneFlag)
            localStorage.removeItem(Constant.localStorageKey.updateDone)
        }
        else {
            if (!online) {
                UpdateStatus.setUpdateStatus(true)
                setIsUpdated(true)
            }
            else {
                setIsUpdated(UpdateStatus.getUpdateStatus())
            }
        }
    }

    const checkUserAuthenticated = () => {
        if (!TranslateStringValue.translate(localStorage.getItem(Constant.localStorageKey.isLoggedIn))) {
            window.location.replace(process.env.PUBLIC_URL + "/login")
        }
    }

    let autoSyncTimeout;
    let autoSyncInterval;

    const setAutoSyncToTrueEveryDay = () => {
        localStorage.setItem(Constant.localStorageKey.autoSync, true);
        window.location.reload();

        const currentDate = new Date().toISOString().split('T')[0];
        localStorage.setItem(Constant.localStorageKey.lastSyncDate, currentDate);
    }

    const scheduleAutoSyncAt9AM = () => {
        const now = new Date();
        const targetTime = new Date(now.getFullYear(), now.getMonth(), now.getDate(), 3, 0, 0);

        // If the target time has already passed for today, set it for tomorrow instead
        if (targetTime <= now) {
            targetTime.setDate(targetTime.getDate() + 1);
        }

        const timeUntilSync = targetTime - now;

        autoSyncTimeout = setTimeout(() => {
            setAutoSyncToTrueEveryDay();
            scheduleAutoSyncEveryDay();
        }, timeUntilSync);
    };

    const scheduleAutoSyncEveryDay = () => {
        autoSyncInterval = setInterval(() => {
            setAutoSyncToTrueEveryDay();
        }, 24 * 60 * 60 * 1000); // 24 hours in milliseconds
    };

    useEffect(() => {
        if (isUpdated) {
            loadData()
        }
    }, [isUpdated])

    useEffect(() => {
        scheduleAutoSyncAt9AM()

        const currentTime = new Date().toTimeString();
        const currentDate = new Date().toISOString().split('T')[0];
        const lastSyncDate = localStorage.getItem(Constant.localStorageKey.lastSyncDate);

        if (currentTime >= "03:00:00" && (lastSyncDate !== currentDate)) {
            setTimeout(() => {
                setAutoSyncToTrueEveryDay();
            });

            localStorage.setItem(Constant.localStorageKey.lastSyncDate, currentDate);
        }

        try {
            if (CheckOs.isIos()) {
                document.addEventListener('mousedown', handleWakeLock);
                document.addEventListener('touchstart', handleWakeLock);
            }
            handleWakeLock();
        } catch (e) {
            console.log("noSleep:", e)
        }

        // <summary> check if user is login
        checkUserAuthenticated()
        // </summary>

        // <summary> update local material and exploitant data when new form submit
        updateRecord()
        // </summary>

        // <summary> check for new update
        checkForAppUpdate()
        const handleUpdateComplete = (e) => {
            localStorage.removeItem(Constant.localStorageKey.updateDone)
            UpdateStatus.setUpdateStatus(true)
            setIsUpdated(true)
        }
        window.addEventListener(Constant.event.updateCompleted, handleUpdateComplete)
        // </summary>

        const onlineHandler = () => {
            submitFormWhenOnline();
        };
        window.addEventListener('online', onlineHandler);

        return () => {
            clearTimeout(autoSyncTimeout);
            clearInterval(autoSyncInterval);
            window.removeEventListener(Constant.event.updateCompleted, handleUpdateComplete)
            window.removeEventListener('online', onlineHandler);
        }
    }, [])

    return <div className={`page ${state && state.prev ? "page--prev" : ""}`}>
        <NavigationBar handleSyncButtonClick={syncData} showToolBar={isUpdated} showNavBar={isUpdated} />
        <CustomizedDialog
            open={alertMessage}
            title={translate('alert')}
            showActionButton={showActionButton}
            closeBtnLabel={dialogBtnLabel}
            onClose={() => {
                setAlertMessage(false);
            }}
        >
            {dialogBody}
        </CustomizedDialog>
        <div className="form-group">
            <h1>{translate('enterReportTitle')}</h1>
            {!isError ? <div className="main">
                {!loading ? <Grid container spacing={4} justifyContent="space-between">
                    {allMaterials.map((material, index) => {
                        return <Grid key={index} item xs={12} md={12} xl={6}>
                            <h5
                                className={`title-bar ${material.category !== "appareils et accessoires de levage" ? "other" : ""}`}
                            >
                                {material.category.toUpperCase()}
                            </h5>
                            <Grid container spacing={2}>
                                {material.elements}
                            </Grid>
                        </Grid>
                    })}
                </Grid>
                    : loadingProgress > -1 ?
                        <ProgressBarComponent value={loadingProgress} message={loadingMessage} />
                        : <LoadingComponent label={loadingText} />
                }
            </div> : <p>Aucun formulaire n'est associé sur ce compte</p>}
            <p style={{ visibility: "hidden" }}>p</p>
        </div>
    </div>
}

export default HomePage;